import moment from 'moment';
import * as objectPath from 'object-path';

import { Precision } from '@ideapa/common-interfaces';

export const isImage = (mimeType: string) => {
  return mimeType?.startsWith('image') || false;
};

export const sort = (
  item1: any,
  item2: any,
  prop: string,
  caseSensitive = false,
  asc = true,
  prop2 = undefined // check two different properties
) => {
  const item2Prop = prop2 || prop;
  let item1Descr = prop.toString().includes('.')
    ? objectPath.get(item1, prop)
    : item1[prop];
  let item2Descr = item2Prop.toString().includes('.')
    ? objectPath.get(item2, item2Prop)
    : item2[item2Prop];
  if (!caseSensitive) {
    item1Descr = item1Descr.toLowerCase();
    item2Descr = item2Descr.toLowerCase();
  }
  if (asc) {
    return item1Descr === item2Descr ? 0 : item1Descr < item2Descr ? -1 : 1;
  } else {
    return item1Descr === item2Descr ? 0 : item1Descr < item2Descr ? 1 : -1;
  }
};

export const kebabize = (str: string) =>
  str.replace(
    /[A-Z]+(?![a-z])|[A-Z]/g,
    ($, ofs) => (ofs ? '-' : '') + $.toLowerCase()
  );

export const camelize = (s: string) =>
  s.replace(/-./g, (x) => x[1].toUpperCase());

export const hasPermission = (
  permission: string,
  entity: string,
  permissions?: string[]
) => {
  const entityName = kebabize(entity.replace('Entity', '')); // entity[0].toLowerCase() + entity.slice(1).replace('Entity', '');
  // console.log('*-*-', permission, entityName, permissions);
  if (!permissions) {
    return false;
  }
  if (permissions?.indexOf(`${permission}_all`) > -1) {
    return true;
  } else {
    return permissions.indexOf(`${permission}_${entityName}`) > -1;
  }
};

export const mergeDateAndTime = (date: string | null, time: string | null) => {
  if (!date || !time) return null;
  const dateString = `${moment(date).format('YYYY-MM-DD')} ${moment(
    time
  ).format('HH:mm:ss')}`;
  return moment(dateString, 'YYYY-MM-DD HH:mm:ss').toISOString();
};

export const getDescendantProp = (obj: any, path: string) => {
  const arr = path.split('.');
  while (arr.length && (obj = obj[arr.shift() as string]));
  return obj;
};
function padTo2Digits(num: number) {
  return num.toString().padStart(2, '0');
}
export function formatDate(date: Date) {
  return [
    date.getFullYear(),
    padTo2Digits(date.getMonth() + 1),
    padTo2Digits(date.getDate()),
  ].join('-');
}
export const isDate = (date: any) => {
  return !isNaN(Date.parse(date));
};

export const downloadFromBuffer = (
  data: ArrayBuffer,
  type: string,
  name: string
) => {
  const blob = new Blob([data], { type: type });
  const url = window.URL.createObjectURL(blob);

  downloadFromUrl(url, name);

  window.URL.revokeObjectURL(url);
};

export const downloadFromUrl = (url: string, name: string) => {
  const link = document.createElement('a');
  link.setAttribute('target', '_blank');
  link.setAttribute('href', url);
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export const currencyPrecision: Precision = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

export const percentPrecision: Precision = {
  minimumFractionDigits: 5,
  maximumFractionDigits: 5,
};

export const percentFormatter = (precision: Precision): Intl.NumberFormat =>
  new Intl.NumberFormat('it-IT', {
    style: 'decimal',
    useGrouping: 'always' as any,
    minimumFractionDigits: precision.minimumFractionDigits,
    maximumFractionDigits: precision.maximumFractionDigits,
  });

export const integerFormatter = new Intl.NumberFormat('it-IT', {
  maximumFractionDigits: 0,
});

// 'it-IT' non raggruppa i numeri a 4 cifre (minimumGroupingDigits = 2) e
// useGrouping: 'always' non e' supportato da tutte le versioni di Node:
// inseriamo il punto delle migliaia a mano (es. 9300,00 -> 9.300,00)
const withFourDigitsGrouping = (
  formatter: Intl.NumberFormat
): Intl.NumberFormat => {
  const format = (value: number | bigint): string =>
    formatter.format(value).replace(/^(\D*)(\d)(\d{3})(?!\d)/, '$1$2.$3');
  return Object.create(formatter, {
    format: { value: format },
  }) as Intl.NumberFormat;
};

export const decimalFormatter = (precision: Precision): Intl.NumberFormat =>
  withFourDigitsGrouping(
    new Intl.NumberFormat('it-IT', {
      style: 'decimal',
      useGrouping: 'always' as any,
      minimumFractionDigits: precision.minimumFractionDigits,
      maximumFractionDigits: precision.maximumFractionDigits,
    })
  );

export const currencyFormatter = withFourDigitsGrouping(
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    useGrouping: 'always' as any,
  })
);

export const sortNumber = (
  item1: any,
  item2: any,
  prop: string,
  asc = true,
  prop2 = undefined // check two different properties
) => {
  const item2Prop = prop2 || prop;
  const item1Descr = prop.toString().includes('.')
    ? +objectPath.get(item1, prop)
    : +item1[prop];
  const item2Descr = item2Prop.toString().includes('.')
    ? +objectPath.get(item2, item2Prop)
    : +item2[item2Prop];
  if (asc) {
    return item1Descr - item2Descr;
  } else {
    return item2Descr - item1Descr;
  }
};
export function downloadFile(blob: Blob, fileName: string) {
  // const blob = new Blob([data], {
  //   type: 'application/xlsx',
  // });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  URL.revokeObjectURL(url);
}

export function normalizeText(text: string) {
  return (
    text
      //   .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, '')
  );
}

/** Sanitizza una stringa da usare come nome file: rimuove diacritici, apostrofi e caratteri
 *  non validi nei filesystem, sostituisce spazi con underscore. */
export function sanitizeFileName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['\u0022]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[<>|:*?\\/]/g, '_');
}
